import { useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import Loading from '@/components/general/Loading';
import useApiFetch from '@/hooks/useApiFetch';
import trendsApi, { GetTrendDetailProps } from '@/services/trends';
import { FetchApiIds } from '@/types';
import { ResponseGetTrendDetail, AnswersWeek } from '@/types/api';
import { DatePeriodFilters } from '@/utils/charts';
import { dateToMonthDayFormat } from '@/utils/format';
import TrendsReportDetailPage from './TrendsReportDetail.component';

const TrendsReportDetailContainer = function (): JSX.Element {
  const { questionId } = useParams();

  const [dateFilter, setDateFilter] = useState<DatePeriodFilters>(DatePeriodFilters.SIX_MONTHS);
  const [currentWeek, setCurrentWeek] = useState<AnswersWeek | null>(null);

  const getTrendDetailResponse = useApiFetch<ResponseGetTrendDetail, GetTrendDetailProps>(
    [FetchApiIds.TRENDS_DETAIL, questionId, dateFilter],
    async () =>
      trendsApi.getTrendDetail({
        questionId: questionId as string,
        month: dateFilter,
      }),
    { enabled: !!questionId }
  );
  const trend = getTrendDetailResponse.data?.trend || null;

  const weekOptions = useMemo(() => {
    if (!trend?.answersWeek?.length) return [];

    return trend.answersWeek.map((answerWeek) => {
      const formattedAnswerWeekStartDate = dateToMonthDayFormat(answerWeek.startDate);
      const formattedAnswerWeekEndDate = dateToMonthDayFormat(answerWeek.endDate);

      return {
        label: `${formattedAnswerWeekStartDate} - ${formattedAnswerWeekEndDate}`,
        value: answerWeek.startDate,
      };
    });
  }, [trend]);

  const handleChangeWeek = (value: string): void => {
    const selectedWeek = trend?.answersWeek?.find((answerWeek) => answerWeek.startDate === value);
    if (selectedWeek) setCurrentWeek(selectedWeek);
  };

  useEffect(() => {
    if (trend?.answersWeek?.length) {
      setCurrentWeek(trend?.answersWeek[0]);
    }
  }, [trend]);

  if (getTrendDetailResponse.isLoading) return <Loading fullPage />;

  return (
    <TrendsReportDetailPage
      trend={trend}
      dateFilter={dateFilter}
      setDateFilter={setDateFilter}
      currentWeek={currentWeek}
      handleChangeWeek={handleChangeWeek}
      weekOptions={weekOptions}
    />
  );
};

export default TrendsReportDetailContainer;
